import * as THREE from 'three'
import { GSPLAT_INSTANCE_SIZE } from '@/plugins/gsplat/core/gsplatBatching'
import type { GSplatInfo } from '@/plugins/gsplat/lod/GSplatInfo'
import { getLoggerManager, isLogMessage } from '@/shared/logging'
import type { GSplatInteractiveSortSchedulerConfig } from '@/shared/types/gsplat'
import { UnifiedSortWorker } from './GSplatUnifiedSortWorker'

/** 排序模块 logger（命名空间 gsplat:sort），单例稳定，模块加载期捕获安全 */
const log = getLoggerManager().getLogger('gsplat:sort')

const unifiedSorterMemoryTotals = {
  workersCreated: 0,
  workersTerminated: 0,
  blobUrlsCreated: 0,
  blobUrlsRevoked: 0,
}
const terminatedUnifiedWorkers = new WeakSet<Worker>()

/** Unified 排序器的进程级 Worker/Blob URL 平衡快照。 */
export interface UnifiedSorterMemoryDiagnostics {
  workersCreated: number
  workersTerminated: number
  workersActive: number
  blobUrlsCreated: number
  blobUrlsRevoked: number
  blobUrlsActive: number
}

export function getUnifiedSorterMemoryDiagnostics(): Readonly<UnifiedSorterMemoryDiagnostics> {
  return {
    ...unifiedSorterMemoryTotals,
    workersActive:
      unifiedSorterMemoryTotals.workersCreated - unifiedSorterMemoryTotals.workersTerminated,
    blobUrlsActive:
      unifiedSorterMemoryTotals.blobUrlsCreated - unifiedSorterMemoryTotals.blobUrlsRevoked,
  }
}

function recordUnifiedWorkerTermination(worker: Worker): void {
  if (!terminatedUnifiedWorkers.has(worker)) {
    terminatedUnifiedWorkers.add(worker)
    unifiedSorterMemoryTotals.workersTerminated++
  }
}

/**
 * 排序参数负载（传递给排序器的配置）
 */
export interface SortParametersPayload {
  /** 排序纹理宽度（每行的 splat 数量） */
  textureSize: number
  /** 每个 splat 的区间数组（扁平化数组，每两个元素一对） */
  intervals: number[][]
  /** 每个 active interval 在 WorkBuffer 中的首个 texel offset。 */
  pixelOffsets: number[][]
  /** 排序结果版本号 */
  version: number
  /** active splat 数量（不包含 WorkBuffer 行 padding） */
  totalActiveSplats: number
  /** 每个 splat 的资源 ID */
  ids: number[]
}

export interface SortVisibilityCompactOptions {
  enabled: boolean
  behindMargin: number
  screenCompact?: boolean
  screenMargin?: number
}

export interface SortVisibilityCompactStats {
  totalActiveSplats: number
  visibleSplats: number
  culledSplats: number
  timeMs: number
  forwardBatchInstanceCount: number
}

export type SortRequestStatus = 'submitted' | 'queued' | 'rejected'

export interface SortRequestResult {
  status: SortRequestStatus
  sortGeneration?: number
  /** 同一组失败参数仍处于退避或已熔断；调用方应停止逐帧原样提交。 */
  retrySuppressed?: boolean
}

export interface SortResultMetadata {
  releaseOrderData?: () => void
  submittedAtMs: number | null
  completedAtMs: number
  workerTimeMs: number
  requestToSubmitLatencyMs: number | null
  bufferReused: boolean
}

export interface GSplatUnifiedSorterDiagnostics {
  submittedRequestCount: number
  queuedRequestCount: number
  replayedRequestCount: number
  overwrittenRequestCount: number
  rejectedRequestCount: number
  staleResultCount: number
  coalescedResultCount: number
  workerErrorCount: number
  lastWorkerSortTimeMs: number
  totalWorkerSortTimeMs: number
  pendingRequestAgeMs: number | null
  lastRequestToSubmitLatencyMs: number | null
  orderBufferAllocCount: number
  orderBufferReuseCount: number
  orderBufferReleaseCount: number
  pendingOrderBufferLeaseCount: number
  adaptiveReplayDeferralCount: number
  adaptiveReplayTimerFireCount: number
  adaptiveSortIntervalMs: number
  workerSortTimeEmaMs: number
  pendingReplayDelayMs: number | null
}

export interface GSplatUnifiedSorterOptions {
  interactiveSortScheduler?: GSplatInteractiveSortSchedulerConfig
  nowMs?: () => number
}

type SortBoundsSource = 'splat' | 'chunk' | 'global'

interface SortBoundsResult {
  data: Float32Array
  source: SortBoundsSource
}

interface SplatDataLike {
  numSplats: number
  getProp<T = Float32Array>(name: string): T | null
}

interface CompressedSplatDataLike {
  numSplats: number
  chunkData: Float32Array
  vertexData: Uint32Array
  chunkSize: number
  numChunks: number
}

interface IteratorSplatLike {
  scale?: [number, number, number] | number[]
  opacity?: number
}

interface IteratorDataLike {
  numSplats: number
  createIterator(): {
    read(index: number): IteratorSplatLike
  }
}

interface SogsMetaBoundsLike {
  scales?: {
    maxs?: [number, number, number] | number[]
    codebook?: number[] | Float32Array
  }
  sh0?: {
    maxs?: [number, number, number, number] | number[]
  }
}

const SORT_BOUNDS_STRIDE = 4
const SORT_BOUND_HAS_RADIUS = 1
const SORT_BOUND_HAS_OPACITY = 2
const SORT_BOUND_HAS_COVARIANCE = 4
const SORT_RADIUS_SIGMA = 2
const COMPRESSED_SPLATS_PER_CHUNK = 256

/**
 * 每个 Splat 的排序参数（转换到相机空间）
 */
export interface GSplatPreparedSortParams {
  /** Resource ID（用于查找 centers） */
  id: number
  /** 相机方向（在模型空间）用于 Linear Sort */
  transformedDirection: THREE.Vector3
  /** 相机位置（在模型空间）用于 Radial Sort */
  transformedPosition: THREE.Vector3
  /** Offset（投影深度偏移） */
  offset: number
  /** Scale（世界空间到投影空间的缩放） */
  scale: number
  /** AABB 最小值（本地空间） */
  aabbMin: [number, number, number]
  /** AABB 最大值（本地空间） */
  aabbMax: [number, number, number]
  /** 冻结 PlayCanvas Manager 提交的 world model matrix 副本。 */
  modelMatrix: number[]
  /** 本地 visibility-compact 扩展使用；canonical Worker 升级后移除。 */
  modelViewProjection?: number[]
}

export interface GSplatSorterResource {
  readonly id: number
  readonly centers?: Float32Array | null
}

export interface GSplatSorterSplat {
  readonly resource: GSplatSorterResource
}

/**
 * 排序完成回调类型
 */
export type OnSortedCallback = (
  count: number,
  version: number,
  orderData: Uint32Array,
  sortGeneration: number,
  metadata?: SortResultMetadata
) => void

export type OnPendingSortedCallback = () => void

export type OnSortJobSettledCallback = () => void

interface PendingSortRequest {
  sortParams: GSplatPreparedSortParams[]
  version: number
  /** 每次 setSortParameters 都递增，用于隔离同 version 下的有效参数更新。 */
  parameterEpoch: number
  textureSize: number
  bufferLength: number
  radialSorting: boolean
  visibilityCompact?: SortVisibilityCompactOptions
  queuedAtMs: number
}

interface SortJobMetadata {
  submittedAtMs: number
  requestToSubmitLatencyMs: number | null
  bufferReused: boolean
  totalActiveSplats: number
  version: number
  parameterEpoch: number
}

function createDefaultNowMs(): () => number {
  const perf = globalThis.performance
  if (perf && typeof perf.now === 'function') {
    return () => perf.now()
  }

  return () => Date.now()
}

type NormalizedInteractiveSortSchedulerConfig = Required<GSplatInteractiveSortSchedulerConfig>

const DEFAULT_INTERACTIVE_SORT_SCHEDULER: NormalizedInteractiveSortSchedulerConfig = {
  mode: 'off',
  targetWorkerDutyCycle: 0.7,
  minIntervalMs: 120,
  maxIntervalMs: 500,
  settleDelayMs: 50,
}

function normalizeInteractiveSortSchedulerConfig(
  config?: GSplatInteractiveSortSchedulerConfig
): NormalizedInteractiveSortSchedulerConfig {
  const minIntervalMs = normalizeNonNegativeNumber(
    config?.minIntervalMs,
    DEFAULT_INTERACTIVE_SORT_SCHEDULER.minIntervalMs
  )
  const maxIntervalMs = Math.max(
    minIntervalMs,
    normalizeNonNegativeNumber(
      config?.maxIntervalMs,
      DEFAULT_INTERACTIVE_SORT_SCHEDULER.maxIntervalMs
    )
  )
  const rawDutyCycle = Number(config?.targetWorkerDutyCycle)
  const targetWorkerDutyCycle = Number.isFinite(rawDutyCycle)
    ? Math.min(1, Math.max(0.1, rawDutyCycle))
    : DEFAULT_INTERACTIVE_SORT_SCHEDULER.targetWorkerDutyCycle

  return {
    mode: config?.mode === 'adaptive' ? 'adaptive' : 'off',
    targetWorkerDutyCycle,
    minIntervalMs,
    maxIntervalMs,
    settleDelayMs: normalizeNonNegativeNumber(
      config?.settleDelayMs,
      DEFAULT_INTERACTIVE_SORT_SCHEDULER.settleDelayMs
    ),
  }
}

function normalizeNonNegativeNumber(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : fallback
}

function normalizeWorkloadSize(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0
}

/**
 * 高性能统一 Splat 排序器（对齐 PlayCanvas UnifiedSorter）
 *
 * 核心特性：
 * 1. **多 Splat 统一排序** - 支持同时排序多个 GSplat 资源
 * 2. **Centers 复用管理** - 基于 resource.id 复用 centers 数据
 * 3. **版本化排序结果** - 忽略过期的排序结果
 * 4. **Buffer 池管理** - 复用 orderData buffer 减少 GC
 * 5. **双排序模式** - Linear Sort（方向投影）/ Radial Sort（径向距离）
 * 6. **分层精度权重** - 相机附近 40x 精度，远处 1x 精度
 * 7. **AABB 优化** - 使用 8 角投影快速计算距离范围
 * 8. **Web Worker 异步排序** - 避免阻塞主线程
 *
 * @remarks
 * 对应 PlayCanvas 的 GSplatUnifiedSorter，使用 Web Worker 异步排序
 */
export class GSplatUnifiedSorter {
  /**
   * Web Worker 实例
   */
  private worker: Worker
  /** 当前 worker 是否不可用。恢复失败时请求会转为 rejected。 */
  private workerFailed = false

  /** 连续重建尝试次数上限，超过后放弃重建避免无限重建风暴 */
  private static readonly MAX_WORKER_RECOVERY_ATTEMPTS = 3

  /** 连续重建尝试次数，收到任意 worker 消息（worker 存活）后归零 */
  private workerRecoveryAttempts = 0

  /** worker 重建成功后置位，由 manager 消费以重发 centers 与世界状态 */
  private workerRecoveredPendingResync = false

  /** 异步排序任务失败后置位，由 manager 消费以重新提交排序 */
  private sortRetryNeeded = false

  /** 同一组有效排序参数最多自动重试两次，避免确定性错误或 OOM 形成热循环。 */
  private static readonly SORT_FAILURE_RETRY_BACKOFF_MS = [250, 1000] as const

  /** 当前失败熔断所属的排序参数 epoch。 */
  private sortFailureParameterEpoch: number | null = null

  /** 当前参数 epoch 下连续收到 failed 结果的次数。 */
  private sortFailureCount = 0

  /** 退避结束后仅授权一次同参数重试。 */
  private sortRetryPermitParameterEpoch: number | null = null

  private sortFailureRetryTimer: ReturnType<typeof setTimeout> | null = null

  /**
   * Centers Set（记录已添加的 resource ID）
   */
  private centersSet = new Set<number>()

  /** 当前是否需要为 worker 维护可见性 compact bounds。 */
  private sortBoundsEnabled: boolean | null = null

  private getSortBoundsForResource(resource: GSplatInfo['resource']): SortBoundsResult | null {
    const cached = this.sortBoundsCache.get(resource.id)
    if (cached) {
      return cached
    }

    const result = this.createSortBoundsForResource(resource)
    if (result) {
      this.sortBoundsCache.set(resource.id, result)
    }

    return result
  }

  private createSortBoundsForResource(resource: GSplatInfo['resource']): SortBoundsResult | null {
    const data = this.getResourceData(resource)
    const count = this.getResourceSplatCount(resource, data)
    if (count <= 0) {
      return null
    }

    if (this.isSplatDataLike(data)) {
      const bounds = this.createBoundsFromSplatData(data, count)
      return bounds ? { data: bounds, source: 'splat' } : null
    }

    if (this.isCompressedSplatDataLike(data)) {
      const bounds = this.createBoundsFromCompressedData(data, count)
      return bounds ? { data: bounds, source: 'chunk' } : null
    }

    if (this.isIteratorDataLike(data)) {
      try {
        const bounds = this.createBoundsFromIteratorData(data, count)
        return bounds ? { data: bounds, source: 'splat' } : null
      } catch {
        // CPU 数据可能已进入 minimal-memory 模式，后续尝试 meta 级全局上界。
      }
    }

    const meta = this.getResourceMeta(resource, data)
    const bounds = this.createBoundsFromSogsMeta(meta, count)
    return bounds ? { data: bounds, source: 'global' } : null
  }

  private createBoundsFromSplatData(data: SplatDataLike, count: number): Float32Array | null {
    const scaleX = data.getProp('scale_0')
    const scaleY = data.getProp('scale_1')
    const scaleZ = data.getProp('scale_2')
    if (!scaleX || !scaleY || !scaleZ) {
      return null
    }

    const opacity = data.getProp('opacity')
    const bounds = new Float32Array(count * SORT_BOUNDS_STRIDE)
    for (let i = 0; i < count; i++) {
      const maxLogScale = Math.max(scaleX[i], scaleY[i], scaleZ[i])
      const maxScale = Number.isFinite(maxLogScale) ? Math.exp(maxLogScale) : null
      const opacityUpper = opacity ? this.sigmoid(opacity[i]) : null
      this.writeSortBound(bounds, i, maxScale, opacityUpper)
    }

    return bounds
  }

  private createBoundsFromCompressedData(
    data: CompressedSplatDataLike,
    count: number
  ): Float32Array | null {
    if (data.chunkSize < 12 || data.numChunks <= 0) {
      return null
    }

    const bounds = new Float32Array(count * SORT_BOUNDS_STRIDE)
    for (let chunkIndex = 0; chunkIndex < data.numChunks; chunkIndex++) {
      const chunkOffset = chunkIndex * data.chunkSize
      const maxLogScale = Math.max(
        data.chunkData[chunkOffset + 9],
        data.chunkData[chunkOffset + 10],
        data.chunkData[chunkOffset + 11]
      )
      const maxScale = Number.isFinite(maxLogScale) ? Math.exp(maxLogScale) : null
      const start = chunkIndex * COMPRESSED_SPLATS_PER_CHUNK
      const end = Math.min(start + COMPRESSED_SPLATS_PER_CHUNK, count)

      for (let i = start; i < end; i++) {
        const packedColor = data.vertexData[i * 4 + 3] ?? 0xffffffff
        const opacityUpper = (packedColor & 0xff) / 255
        this.writeSortBound(bounds, i, maxScale, opacityUpper)
      }
    }

    return bounds
  }

  private createBoundsFromIteratorData(data: IteratorDataLike, count: number): Float32Array | null {
    const iterator = data.createIterator()
    const bounds = new Float32Array(count * SORT_BOUNDS_STRIDE)

    for (let i = 0; i < count; i++) {
      const splat = iterator.read(i)
      const maxScale = this.getMaxScaleFromLogScaleArray(splat.scale)
      const opacityUpper =
        splat.opacity === undefined ? null : this.normalizeOpacityUpper(splat.opacity)
      this.writeSortBound(bounds, i, maxScale, opacityUpper)
    }

    return bounds
  }

  private createBoundsFromSogsMeta(
    meta: SogsMetaBoundsLike | null,
    count: number
  ): Float32Array | null {
    const maxScale = this.getMaxScaleFromSogsMeta(meta)
    if (maxScale === null) {
      return null
    }

    const opacityUpper = this.getOpacityUpperFromSogsMeta(meta)
    const bounds = new Float32Array(count * SORT_BOUNDS_STRIDE)
    for (let i = 0; i < count; i++) {
      this.writeSortBound(bounds, i, maxScale, opacityUpper)
    }

    return bounds
  }

  private writeSortBound(
    bounds: Float32Array,
    index: number,
    maxScale: number | null,
    opacityUpper: number | null
  ): void {
    const base = index * SORT_BOUNDS_STRIDE
    let flags = 0

    if (maxScale !== null && Number.isFinite(maxScale) && maxScale > 0) {
      bounds[base] = maxScale * SORT_RADIUS_SIGMA
      bounds[base + 2] = maxScale * maxScale
      flags |= SORT_BOUND_HAS_RADIUS | SORT_BOUND_HAS_COVARIANCE
    }

    const normalizedOpacity = this.normalizeOpacityUpper(opacityUpper)
    if (normalizedOpacity !== null) {
      bounds[base + 1] = normalizedOpacity
      flags |= SORT_BOUND_HAS_OPACITY
    } else {
      bounds[base + 1] = 1
    }

    bounds[base + 3] = flags
  }

  private getMaxScaleFromLogScaleArray(scale: IteratorSplatLike['scale']): number | null {
    if (!scale || scale.length < 3) {
      return null
    }

    const maxLogScale = Math.max(scale[0], scale[1], scale[2])
    return Number.isFinite(maxLogScale) ? Math.exp(maxLogScale) : null
  }

  private getMaxScaleFromSogsMeta(meta: SogsMetaBoundsLike | null): number | null {
    if (!meta?.scales) {
      return null
    }

    let maxLogScale = -Infinity
    const maxs = meta.scales.maxs
    if (maxs) {
      for (const value of maxs) {
        if (Number.isFinite(value) && value > maxLogScale) {
          maxLogScale = value
        }
      }
    }

    const codebook = meta.scales.codebook
    if (codebook) {
      for (const value of codebook) {
        if (Number.isFinite(value) && value > maxLogScale) {
          maxLogScale = value
        }
      }
    }

    return Number.isFinite(maxLogScale) ? Math.exp(maxLogScale) : null
  }

  private getOpacityUpperFromSogsMeta(meta: SogsMetaBoundsLike | null): number | null {
    const alphaMax = meta?.sh0?.maxs?.[3]
    return alphaMax === undefined ? null : this.normalizeOpacityUpper(alphaMax)
  }

  private normalizeOpacityUpper(value: number | null): number | null {
    if (value === null || !Number.isFinite(value)) {
      return null
    }

    if (value < 0 || value > 1) {
      return this.sigmoid(value)
    }

    return Math.min(1, Math.max(0, value))
  }

  private sigmoid(value: number): number {
    if (!Number.isFinite(value)) {
      return 1
    }

    if (value > 0) {
      return 1 / (1 + Math.exp(-value))
    }

    const t = Math.exp(value)
    return t / (1 + t)
  }

  private getResourceData(resource: GSplatInfo['resource']): unknown {
    const typedResource = resource as { data?: unknown; gsplatData?: unknown }
    return typedResource.data ?? typedResource.gsplatData ?? null
  }

  private getResourceSplatCount(resource: GSplatInfo['resource'], data: unknown): number {
    const resourceCount = Number((resource as { numSplats?: number }).numSplats)
    if (Number.isFinite(resourceCount) && resourceCount > 0) {
      return Math.floor(resourceCount)
    }

    if (this.isObject(data)) {
      const dataCount = Number((data as { numSplats?: number }).numSplats)
      if (Number.isFinite(dataCount) && dataCount > 0) {
        return Math.floor(dataCount)
      }
    }

    return Math.floor((resource.centers?.length ?? 0) / 3)
  }

  private getResourceMeta(
    resource: GSplatInfo['resource'],
    data: unknown
  ): SogsMetaBoundsLike | null {
    const resourceMeta = (resource as { meta?: unknown }).meta
    if (this.isSogsMetaBoundsLike(resourceMeta)) {
      return resourceMeta
    }

    if (this.isObject(data)) {
      const dataMeta = (data as { meta?: unknown }).meta
      if (this.isSogsMetaBoundsLike(dataMeta)) {
        return dataMeta
      }
    }

    return null
  }

  private isSplatDataLike(data: unknown): data is SplatDataLike {
    return this.isObject(data) && typeof (data as unknown as SplatDataLike).getProp === 'function'
  }

  private isCompressedSplatDataLike(data: unknown): data is CompressedSplatDataLike {
    if (!this.isObject(data)) {
      return false
    }

    const typedData = data as Partial<CompressedSplatDataLike>
    return (
      typedData.chunkData instanceof Float32Array &&
      typedData.vertexData instanceof Uint32Array &&
      Number.isFinite(typedData.chunkSize) &&
      Number.isFinite(typedData.numChunks) &&
      Number.isFinite(typedData.numSplats)
    )
  }

  private isIteratorDataLike(data: unknown): data is IteratorDataLike {
    return (
      this.isObject(data) &&
      typeof (data as unknown as IteratorDataLike).createIterator === 'function'
    )
  }

  private isSogsMetaBoundsLike(value: unknown): value is SogsMetaBoundsLike {
    return this.isObject(value) && this.isObject((value as { scales?: unknown }).scales)
  }

  private isObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null
  }

  /**
   * 排序侧可见性 bounds 缓存，按 resource.id 复用。
   */
  private sortBoundsCache = new Map<number, SortBoundsResult>()

  /**
   * 排序参数缓存（每次 setSortParameters 时更新）
   */
  private sortParams: SortParametersPayload | null = null

  /**
   * 当前排序版本号（每次 setSortParameters 时递增）
   */
  private currentVersion = 0

  /** 有效排序参数代次；即使业务 version 复用，也能隔离迟到的旧结果。 */
  private sortParametersEpoch = 0

  /**
   * 是否有新版本待处理
   */
  private hasNewVersion = false

  /**
   * 正在飞行中的排序任务数量（用于节流）
   */
  jobsInFlight = 0

  /**
   * 待应用的排序结果（接收到的最新排序结果）
   */
  private pendingSorted: {
    count: number
    version: number
    orderData: Uint32Array
    sortGeneration: number
    submittedAtMs: number | null
    completedAtMs: number
    workerTimeMs: number
    requestToSubmitLatencyMs: number | null
    bufferReused: boolean
  } | null = null

  private pendingSortRequest: PendingSortRequest | null = null

  get hasPendingSort(): boolean {
    return this.pendingSorted !== null
  }

  private nextSortGeneration = 1

  private latestAcceptedSortGeneration = 0

  private _staleResultCount = 0

  private _coalescedResultCount = 0

  private _submittedRequestCount = 0

  private _queuedRequestCount = 0

  private _replayedRequestCount = 0

  private _overwrittenRequestCount = 0

  private _rejectedRequestCount = 0

  private _workerErrorCount = 0

  private _lastWorkerSortTimeMs = 0

  private _totalWorkerSortTimeMs = 0

  private _lastRequestToSubmitLatencyMs: number | null = null

  private _orderBufferAllocCount = 0

  private _orderBufferReuseCount = 0

  private _orderBufferReleaseCount = 0

  /**
   * 可用的 orderData buffer 池
   */
  private availableOrderData: Uint32Array[] = []

  private leasedOrderData = new Set<Uint32Array>()

  private inFlightSorts = new Map<number, SortJobMetadata>()

  private sortNowMs: () => number

  private interactiveSortScheduler: NormalizedInteractiveSortSchedulerConfig

  private lastSortSubmittedAtMs = Number.NEGATIVE_INFINITY

  private workerSortTimeEmaMs = 0

  private workerSortTimePerSplatEmaMs = 0

  private currentSortWorkloadSplats = 0

  private pendingReplayTimer: ReturnType<typeof setTimeout> | null = null

  private pendingReplayScheduledAtMs: number | null = null

  private _adaptiveReplayDeferralCount = 0

  private _adaptiveReplayTimerFireCount = 0

  private _lastAdaptiveSortIntervalMs = 0

  /**
   * 当前 buffer 长度（textureSize * textureSize）
   */
  private bufferLength = 0

  /**
   * 排序完成回调
   */
  private onSortedCallback: OnSortedCallback | null = null

  /**
   * Worker 结果已返回但尚未在帧循环中 apply 的回调。
   */
  private onPendingSortedCallback: OnPendingSortedCallback | null = null

  /**
   * 排序任务结束后触发，无论结果被接收、丢弃还是失败。
   */
  private onSortJobSettledCallback: OnSortJobSettledCallback | null = null

  /**
   * 最近一次排序 compact 统计。
   */
  private _lastVisibilityCompactStats: SortVisibilityCompactStats = {
    totalActiveSplats: 0,
    visibleSplats: 0,
    culledSplats: 0,
    timeMs: 0,
    forwardBatchInstanceCount: 0,
  }

  /**
   * 是否已销毁
   */
  private _destroyed = false

  /** 首次排序计时 end 函数，首个 sort 请求时由 perf 创建，首个有效结果回传时调用 */
  private _firstSortEnd: (() => number) | null = null
  /** 是否已发起过首次排序请求 */
  private _firstSortStarted = false

  /**
   * 排序模式：false = Linear Sort，true = Radial Sort
   */
  private radialSort = false

  // 临时变量（避免每帧分配）
  private _tempVec3 = new THREE.Vector3()
  private _tempVec3_2 = new THREE.Vector3()
  private _tempWorldCameraPos = new THREE.Vector3()
  private _tempWorldCameraDir = new THREE.Vector3()
  private _tempWorldModelPos = new THREE.Vector3()
  private _invModelMatrix = new THREE.Matrix4()
  private _viewProjectionMatrix = new THREE.Matrix4()
  private _modelViewProjectionMatrix = new THREE.Matrix4()

  /**
   * 构造函数
   */
  constructor(options: GSplatUnifiedSorterOptions = {}) {
    this.sortNowMs = options.nowMs ?? createDefaultNowMs()
    this.interactiveSortScheduler = normalizeInteractiveSortSchedulerConfig(
      options.interactiveSortScheduler
    )
    this.worker = this.createWorker()
  }

  private createWorker(): Worker {
    const workerSource = `(${UnifiedSortWorker.toString()})()`
    const blob = new Blob([workerSource], { type: 'application/javascript' })
    const blobUrl = URL.createObjectURL(blob)
    unifiedSorterMemoryTotals.blobUrlsCreated++

    try {
      const worker = new Worker(blobUrl)
      unifiedSorterMemoryTotals.workersCreated++
      this.bindWorkerEvents(worker)
      this.workerFailed = false
      return worker
    } catch (error) {
      log.error(`创建排序 worker 失败: ${String(error)}`)
      this.workerFailed = true
      throw error
    } finally {
      URL.revokeObjectURL(blobUrl)
      unifiedSorterMemoryTotals.blobUrlsRevoked++
    }
  }

  private bindWorkerEvents(worker: Worker): void {
    worker.addEventListener('message', this.onWorkerMessage.bind(this))
    worker.addEventListener('error', (error) => {
      log.error(`排序 worker 错误: ${String(error)}`)
      this.handleWorkerFailure()
    })
    worker.addEventListener('messageerror', (error) => {
      log.error(`排序 worker 消息错误: ${String(error)}`)
      this.handleWorkerFailure()
    })
  }

  /**
   * 设置排序完成回调。
   *
   * @param callback - 排序完成时的回调，接收 Worker 产出的结果
   */
  setOnSortedCallback(callback: OnSortedCallback): void {
    this.onSortedCallback = callback
  }

  /**
   * 设置排序结果待应用回调。
   *
   * @param callback - Worker 有新排序结果进入 pending 队列时触发
   */
  setOnPendingSortedCallback(callback: OnPendingSortedCallback | null): void {
    this.onPendingSortedCallback = callback
  }

  /**
   * 设置排序任务结算回调。
   *
   * @param callback - Worker 排序任务结束或 Worker 恢复完成时触发
   */
  setOnSortJobSettledCallback(callback: OnSortJobSettledCallback | null): void {
    this.onSortJobSettledCallback = callback
  }

  setInteractiveSortSchedulerConfig(config?: GSplatInteractiveSortSchedulerConfig): void {
    this.interactiveSortScheduler = normalizeInteractiveSortSchedulerConfig(config)
    this.clearPendingReplayTimer()
    this.trySubmitPendingReplay()
  }

  /**
  
  /**
   * 绑定事件（PlayCanvas 兼容接口）
   * @param event - 事件名称（当前仅支持 'sorted'）
   * @param callback - 回调函数
   */

  on(event: string, callback: OnSortedCallback): void {
    if (event === 'sorted') {
      this.setOnSortedCallback(callback)
    } else {
      log.warn(`不支持的事件: ${event}`)
    }
  }

  /**
   * 设置排序模式
   * @param radial - true 为 Radial Sort，false 为 Linear Sort
   */
  setRadialSort(radial: boolean): void {
    this.radialSort = radial
  }

  /**
   * 按冻结 PlayCanvas 协议增删单个 Resource centers。
   *
   * 只有 postMessage 成功后才推进 centersSet，使 Manager 能够在
   * `null → centers` 的任一步失败后保留自己的 centersVersion baseline 并重试。
   */
  setCenters(id: number, centers: Float32Array | null): void {
    if (this._destroyed || this.workerFailed) {
      throw new Error('GSplat UnifiedSorter 不可用，无法更新 centers')
    }
    if (!Number.isSafeInteger(id) || id < 0) throw new Error(`非法 GSplat Resource id: ${id}`)

    if (centers) {
      if (this.centersSet.has(id)) return
      this.postCenters(id, centers)
      this.centersSet.add(id)
      return
    }

    if (!this.centersSet.has(id)) return
    try {
      this.worker.postMessage({ command: 'removeCenters', id })
    } catch (error) {
      this.handleWorkerFailure()
      throw new Error(`GSplat centers 移除失败: id=${id}`, { cause: error })
    }
    this.centersSet.delete(id)
    this.sortBoundsCache.delete(id)
  }

  /**
   * 更新 Centers 数据（从多个 splat 信息中收集）
   *
   * @param splats - 当前激活的 splat 信息数组
   *
   * @remarks
   * PlayCanvas 策略：
   * 1. 添加新出现的 resource centers 到 Worker
   * 2. 移除不再需要的 centers
   */
  updateCentersForSplats(
    splats: readonly GSplatSorterSplat[],
    includeSortBounds: boolean = true
  ): void {
    if (this._destroyed || this.workerFailed) {
      throw new Error('GSplat UnifiedSorter 不可用，无法同步 centers')
    }

    const sortBoundsModeChanged =
      this.sortBoundsEnabled !== null && this.sortBoundsEnabled !== includeSortBounds
    this.sortBoundsEnabled = includeSortBounds

    if (!includeSortBounds || sortBoundsModeChanged) {
      this.sortBoundsCache.clear()
    }

    // 构建当前需要的 resource ID 集合
    const neededIds = new Set<number>()
    for (const splat of splats) {
      const id = splat.resource.id
      const isFirstOccurrence = !neededIds.has(id)
      neededIds.add(id)

      // 新资源需要发送 centers；compact bounds 模式切换时也需要重发，
      // 使 worker 能够添加新 bounds 或删除已存在的 bounds。
      if (isFirstOccurrence && (!this.centersSet.has(id) || sortBoundsModeChanged)) {
        const centers = splat.resource.centers
        if (!centers) throw new Error(`GSplat Resource[${id}] 缺少 CPU centers`)
        if (this.centersSet.has(id)) this.setCenters(id, null)
        const boundsResult = includeSortBounds
          ? this.getSortBoundsForResource(splat.resource as GSplatInfo['resource'])
          : null
        this.postCenters(id, centers, boundsResult?.data)
        this.centersSet.add(id)
      }
    }

    // 移除不再需要的 centers
    for (const id of this.centersSet) {
      if (!neededIds.has(id)) {
        this.setCenters(id, null)
      }
    }
  }

  private postCenters(id: number, centers: Float32Array, bounds?: Float32Array): void {
    const centersBuffer = centers.slice().buffer as ArrayBuffer
    const message: {
      command: 'addCenters'
      id: number
      centers: ArrayBuffer
      bounds?: ArrayBuffer
    } = { command: 'addCenters', id, centers: centersBuffer }
    const transfer: Transferable[] = [centersBuffer]
    if (bounds) {
      const boundsBuffer = bounds.slice().buffer as ArrayBuffer
      message.bounds = boundsBuffer
      transfer.push(boundsBuffer)
    }
    try {
      this.worker.postMessage(message, transfer)
    } catch (error) {
      this.handleWorkerFailure()
      throw new Error(`GSplat centers 提交失败: id=${id}`, { cause: error })
    }
  }

  /**
   * 设置排序参数（每次场景结构变化时调用）
   *
   * @param payload - 排序参数负载
   *
   * @remarks
   * PlayCanvas 策略：
   * 1. 接收参数后标记有新版本
   * 2. 如果 buffer 大小变化，清空复用池
   * 3. 发送 intervals 数据到 Worker
   */
  setSortParameters(payload: SortParametersPayload): void {
    if (this._destroyed) return

    this.clearPendingReplayTimer()
    this.resetSortFailureRetryState()
    if (this.pendingSortRequest) {
      this.pendingSortRequest = null
      this._overwrittenRequestCount++
    }

    // 标记有新版本
    this.hasNewVersion = true
    this.currentVersion = payload.version
    this.sortParametersEpoch++
    this.currentSortWorkloadSplats = normalizeWorkloadSize(payload.totalActiveSplats)
    this.refreshWorkerSortTimeEstimate()

    // 存储排序参数
    this.sortParams = payload

    // 输出 buffer 大小匹配输入大小，如果大小变化则清空可用数据
    const newLength = payload.textureSize * payload.textureSize
    if (newLength !== this.bufferLength) {
      this.bufferLength = newLength
      this.availableOrderData.length = 0
    }

    this.postSortParametersToWorker(payload)
  }

  private postSortParametersToWorker(payload: SortParametersPayload): void {
    if (this.workerFailed) {
      return
    }

    try {
      this.worker.postMessage({
        command: 'intervals',
        textureSize: payload.textureSize,
        ids: payload.ids,
        pixelOffsets: payload.pixelOffsets,
        intervals: payload.intervals,
        totalActiveSplats: payload.totalActiveSplats,
        version: payload.version,
      })
    } catch {
      this.handleWorkerFailure()
    }
  }

  /**
   * 触发排序（每帧调用）
   *
   * @param camera - 当前相机
   * @param splats - 当前激活的 splat 信息数组
   *
   * @remarks
   * PlayCanvas 策略：
   * 1. 只有在有新版本或无飞行任务时才处理
   * 2. 从池中获取或分配新的 orderData
   * 3. 发送排序任务到 Worker（使用 transferable）
   */
  setSortParams(
    camera: THREE.Camera,
    splats: GSplatInfo[],
    visibilityCompact?: SortVisibilityCompactOptions
  ): SortRequestResult {
    if (this._destroyed || this.workerFailed || !this.sortParams) {
      this._rejectedRequestCount++
      return { status: 'rejected' }
    }
    return this.enqueueSortRequest(this.createPendingSortRequest(camera, splats, visibilityCompact))
  }

  /**
   * 接收 Manager 按 WorldState 顺序准备的上游形状参数。
   *
   * 该入口避免 sorter 再次从可变 Scene 对象读取 camera/model，保证一次
   * dispatch 使用同一份 operation-local snapshot。
   */
  setPreparedSortParams(
    sortParams: readonly GSplatPreparedSortParams[],
    radialSorting: boolean,
    visibilityCompact?: SortVisibilityCompactOptions
  ): SortRequestResult {
    if (this._destroyed || this.workerFailed || !this.sortParams) {
      this._rejectedRequestCount++
      return { status: 'rejected' }
    }
    const request = this.createPendingSortRequestFromPrepared(
      sortParams,
      radialSorting,
      visibilityCompact
    )
    return this.enqueueSortRequest(request)
  }

  private enqueueSortRequest(request: PendingSortRequest): SortRequestResult {
    if (this._destroyed || this.workerFailed || !this.sortParams) {
      this._rejectedRequestCount++
      return { status: 'rejected' }
    }

    // 默认路径严格保持 PlayCanvas：只有新 world version 或无 in-flight job 才提交。
    // latest queued request 仅属于显式 adaptive scheduler 扩展，不能成为 canonical sorter 状态机。
    if (!this.hasNewVersion && this.jobsInFlight > 0) {
      if (this.interactiveSortScheduler.mode === 'adaptive') {
        this.queuePendingSortRequest(request)
        return { status: 'queued' }
      }
      this._rejectedRequestCount++
      return { status: 'rejected' }
    }

    if (!this.hasNewVersion && this.shouldDeferInteractiveSortRequest(request)) {
      this.queuePendingSortRequest(request)
      this.trySubmitPendingReplay()
      return { status: 'queued' }
    }

    if (this.pendingSortRequest) {
      this.pendingSortRequest = null
      this._overwrittenRequestCount++
    }
    this.clearPendingReplayTimer()

    return this.submitSortRequest(request)
  }

  private createPendingSortRequest(
    camera: THREE.Camera,
    splats: GSplatInfo[],
    visibilityCompact?: SortVisibilityCompactOptions
  ): PendingSortRequest {
    return {
      sortParams: this.prepareSortParams(camera, splats),
      version: this.sortParams?.version ?? this.currentVersion,
      parameterEpoch: this.sortParametersEpoch,
      textureSize: this.sortParams?.textureSize ?? 0,
      bufferLength: this.bufferLength,
      radialSorting: this.radialSort,
      visibilityCompact: visibilityCompact ? { ...visibilityCompact } : undefined,
      queuedAtMs: this.sortNowMs(),
    }
  }

  private createPendingSortRequestFromPrepared(
    sortParams: readonly GSplatPreparedSortParams[],
    radialSorting: boolean,
    visibilityCompact?: SortVisibilityCompactOptions
  ): PendingSortRequest {
    return {
      sortParams: sortParams.map((params) => ({
        ...params,
        transformedDirection: params.transformedDirection.clone(),
        transformedPosition: params.transformedPosition.clone(),
        aabbMin: [...params.aabbMin],
        aabbMax: [...params.aabbMax],
        modelMatrix: [...params.modelMatrix],
        modelViewProjection: params.modelViewProjection
          ? [...params.modelViewProjection]
          : undefined,
      })),
      version: this.sortParams?.version ?? this.currentVersion,
      parameterEpoch: this.sortParametersEpoch,
      textureSize: this.sortParams?.textureSize ?? 0,
      bufferLength: this.bufferLength,
      radialSorting,
      visibilityCompact: visibilityCompact ? { ...visibilityCompact } : undefined,
      queuedAtMs: this.sortNowMs(),
    }
  }

  private queuePendingSortRequest(request: PendingSortRequest): void {
    if (this.pendingSortRequest) {
      this._overwrittenRequestCount++
    }

    this.pendingSortRequest = request
    this._queuedRequestCount++
  }

  private submitSortRequest(
    request: PendingSortRequest,
    replayed: boolean = false
  ): SortRequestResult {
    if (this._destroyed || this.workerFailed || !this.sortParams || request.bufferLength <= 0) {
      this._rejectedRequestCount++
      return { status: 'rejected' }
    }

    if (request.version !== this.currentVersion || request.bufferLength !== this.bufferLength) {
      this._overwrittenRequestCount++
      this._rejectedRequestCount++
      return { status: 'rejected' }
    }

    if (request.parameterEpoch !== this.sortParametersEpoch) {
      this._overwrittenRequestCount++
      this._rejectedRequestCount++
      return { status: 'rejected' }
    }

    if (this.sortFailureParameterEpoch === request.parameterEpoch) {
      if (this.sortRetryPermitParameterEpoch !== request.parameterEpoch) {
        this._rejectedRequestCount++
        return { status: 'rejected', retrySuppressed: true }
      }
      this.sortRetryPermitParameterEpoch = null
    }

    const submittedAtMs = this.sortNowMs()
    const requestToSubmitLatencyMs = Math.max(0, submittedAtMs - request.queuedAtMs)
    const orderBufferResult = this.acquireOrderData()

    // Worker 管理
    const sortGeneration = this.nextSortGeneration++
    this.jobsInFlight++
    this.hasNewVersion = false
    this._submittedRequestCount++
    if (replayed) {
      this._replayedRequestCount++
    }
    this._lastRequestToSubmitLatencyMs = requestToSubmitLatencyMs
    this.inFlightSorts.set(sortGeneration, {
      submittedAtMs,
      requestToSubmitLatencyMs,
      bufferReused: orderBufferResult.reused,
      totalActiveSplats: normalizeWorkloadSize(this.sortParams.totalActiveSplats),
      version: request.version,
      parameterEpoch: request.parameterEpoch,
    })

    try {
      this.worker.postMessage(
        {
          command: 'sort',
          sortParams: request.sortParams,
          radialSorting: request.radialSorting,
          visibilityCompact: request.visibilityCompact,
          sortGeneration,
          order: orderBufferResult.orderData.buffer,
        },
        [orderBufferResult.orderData.buffer]
      )
    } catch {
      this.inFlightSorts.delete(sortGeneration)
      this.jobsInFlight = Math.max(0, this.jobsInFlight - 1)
      this.releaseOrderData(orderBufferResult.orderData)
      this._rejectedRequestCount++
      this.handleWorkerFailure()
      return { status: 'rejected' }
    }

    this.sortRetryNeeded = false
    this.lastSortSubmittedAtMs = submittedAtMs

    // 首次排序请求：启动 firstSort 计时（perf 关闭时为 no-op）
    if (!this._firstSortStarted) {
      this._firstSortStarted = true
      this._firstSortEnd = log.perf.time('firstSort')
    }

    return { status: 'submitted', sortGeneration }
  }

  private acquireOrderData(): { orderData: Uint32Array; reused: boolean } {
    let orderData = this.availableOrderData.pop()
    if (!orderData) {
      orderData = new Uint32Array(this.bufferLength)
      this._orderBufferAllocCount++
      return { orderData, reused: false }
    }

    this._orderBufferReuseCount++
    return { orderData, reused: true }
  }

  private trySubmitPendingReplay(): void {
    if (!this.pendingSortRequest || this.jobsInFlight > 0 || this._destroyed) {
      return
    }

    if (this.interactiveSortScheduler.mode === 'adaptive') {
      const targetAtMs = this.resolvePendingReplayTargetAtMs(this.pendingSortRequest)
      const delayMs = targetAtMs - this.sortNowMs()
      if (delayMs > 0) {
        this.schedulePendingReplay(targetAtMs)
        return
      }
    }

    this.clearPendingReplayTimer()
    const request = this.pendingSortRequest
    this.pendingSortRequest = null
    this.submitSortRequest(request, true)
  }

  private shouldDeferInteractiveSortRequest(request: PendingSortRequest): boolean {
    return (
      this.interactiveSortScheduler.mode === 'adaptive' &&
      this.resolvePendingReplayTargetAtMs(request) > this.sortNowMs()
    )
  }

  private resolvePendingReplayTargetAtMs(request: PendingSortRequest): number {
    const intervalMs = this.resolveAdaptiveSortIntervalMs()
    const cadenceAtMs = Number.isFinite(this.lastSortSubmittedAtMs)
      ? this.lastSortSubmittedAtMs + intervalMs
      : request.queuedAtMs
    const settledAtMs = request.queuedAtMs + this.resolveAdaptiveSettleDelayMs()
    return Math.min(cadenceAtMs, settledAtMs)
  }

  private resolveAdaptiveSettleDelayMs(): number {
    const { maxIntervalMs, settleDelayMs } = this.interactiveSortScheduler
    const workloadAwareDelayMs = Math.min(maxIntervalMs, this.workerSortTimeEmaMs)
    return Math.max(settleDelayMs, workloadAwareDelayMs)
  }

  private resolveAdaptiveSortIntervalMs(): number {
    const { minIntervalMs, maxIntervalMs, targetWorkerDutyCycle } = this.interactiveSortScheduler
    const desiredIntervalMs =
      this.workerSortTimeEmaMs > 0
        ? this.workerSortTimeEmaMs / targetWorkerDutyCycle
        : minIntervalMs
    const intervalMs = Math.min(maxIntervalMs, Math.max(minIntervalMs, desiredIntervalMs))
    this._lastAdaptiveSortIntervalMs = intervalMs
    return intervalMs
  }

  private schedulePendingReplay(targetAtMs: number): void {
    if (
      this.pendingReplayScheduledAtMs !== null &&
      Math.abs(this.pendingReplayScheduledAtMs - targetAtMs) < 1
    ) {
      return
    }

    this.clearPendingReplayTimer()
    this.pendingReplayScheduledAtMs = targetAtMs
    this._adaptiveReplayDeferralCount++
    const delayMs = Math.max(0, Math.ceil(targetAtMs - this.sortNowMs()))
    this.pendingReplayTimer = globalThis.setTimeout(() => {
      this.pendingReplayTimer = null
      this.pendingReplayScheduledAtMs = null
      this._adaptiveReplayTimerFireCount++
      this.trySubmitPendingReplay()
    }, delayMs)
  }

  private clearPendingReplayTimer(): void {
    if (this.pendingReplayTimer !== null) {
      globalThis.clearTimeout(this.pendingReplayTimer)
    }
    this.pendingReplayTimer = null
    this.pendingReplayScheduledAtMs = null
  }

  private updateWorkerSortTimeEma(workerTimeMs: number, totalActiveSplats: number): void {
    const workloadSplats = normalizeWorkloadSize(totalActiveSplats)
    if (!Number.isFinite(workerTimeMs) || workerTimeMs <= 0 || workloadSplats <= 0) {
      return
    }

    const alpha = 0.25
    const sampleTimePerSplatMs = workerTimeMs / workloadSplats
    this.workerSortTimePerSplatEmaMs =
      this.workerSortTimePerSplatEmaMs === 0
        ? sampleTimePerSplatMs
        : this.workerSortTimePerSplatEmaMs * (1 - alpha) + sampleTimePerSplatMs * alpha
    this.refreshWorkerSortTimeEstimate()
  }

  private refreshWorkerSortTimeEstimate(): void {
    this.workerSortTimeEmaMs =
      this.currentSortWorkloadSplats > 0 && this.workerSortTimePerSplatEmaMs > 0
        ? this.workerSortTimePerSplatEmaMs * this.currentSortWorkloadSplats
        : 0
  }

  /**
   * Worker 消息处理（接收排序结果）
   */
  private onWorkerMessage(message: MessageEvent): void {
    if (this._destroyed) return

    // worker 回传日志：转交 LoggerManager 按级别过滤输出，不参与排序流程
    if (isLogMessage(message.data)) {
      getLoggerManager().receiveRecord(message.data.__log__)
      return
    }

    // 收到任意消息说明 worker 线程存活，重置连续重建计数
    this.workerRecoveryAttempts = 0

    const msgData = message.data

    const orderData = new Uint32Array(msgData.order)
    const sortGeneration = Number.isFinite(msgData.sortGeneration)
      ? Math.max(0, Math.floor(msgData.sortGeneration))
      : 0

    // 递减飞行任务计数
    this.jobsInFlight = Math.max(0, this.jobsInFlight - 1)

    const jobMetadata = sortGeneration > 0 ? this.inFlightSorts.get(sortGeneration) : undefined
    if (sortGeneration > 0) {
      this.inFlightSorts.delete(sortGeneration)
    }
    const completedAtMs = this.sortNowMs()
    const workerTimeMs = Number.isFinite(msgData.timeMs) ? Math.max(0, msgData.timeMs) : 0
    this._lastWorkerSortTimeMs = workerTimeMs
    this._totalWorkerSortTimeMs += workerTimeMs

    try {
      if (msgData.failed === true) {
        this._workerErrorCount++
        this._rejectedRequestCount++
        if (jobMetadata?.parameterEpoch === this.sortParametersEpoch) {
          this.hasNewVersion = true
          this.recordSortFailure(jobMetadata.version, jobMetadata.parameterEpoch)
        }
        this.releaseOrderData(orderData)
        return
      }

      if (jobMetadata?.parameterEpoch === this.sortFailureParameterEpoch) {
        this.resetSortFailureRetryState()
      }

      this.updateWorkerSortTimeEma(
        workerTimeMs,
        jobMetadata?.totalActiveSplats ?? msgData.totalActiveSplats ?? 0
      )

      if (
        sortGeneration > 0 &&
        (sortGeneration <= this.latestAcceptedSortGeneration ||
          (this.pendingSorted !== null && sortGeneration <= this.pendingSorted.sortGeneration))
      ) {
        this._staleResultCount++
        this.releaseOrderData(orderData)
        return
      }

      // 首个有效排序结果回传：结束 firstSort 计时（perf 关闭时此前 end 为 no-op）
      if (this._firstSortEnd) {
        this._firstSortEnd()
        this._firstSortEnd = null
      }

      this._lastVisibilityCompactStats = {
        totalActiveSplats:
          msgData.totalActiveSplats ?? this.sortParams?.totalActiveSplats ?? msgData.count,
        visibleSplats: msgData.count,
        culledSplats: msgData.culledSplats ?? 0,
        timeMs: workerTimeMs,
        forwardBatchInstanceCount:
          msgData.count === 0 ? 0 : Math.ceil(msgData.count / GSPLAT_INSTANCE_SIZE),
      }

      // 如果已经有待处理的结果，将其返回池中
      if (this.pendingSorted) {
        this._coalescedResultCount++
        this.releaseOrderData(this.pendingSorted.orderData)
      }

      // 存储新的结果
      this.pendingSorted = {
        count: msgData.count,
        version: msgData.version,
        orderData: orderData,
        sortGeneration,
        submittedAtMs: jobMetadata?.submittedAtMs ?? null,
        completedAtMs,
        workerTimeMs,
        requestToSubmitLatencyMs: jobMetadata?.requestToSubmitLatencyMs ?? null,
        bufferReused: jobMetadata?.bufferReused ?? false,
      }
      if (sortGeneration > 0) {
        this.latestAcceptedSortGeneration = sortGeneration
      }
      this.onPendingSortedCallback?.()
    } finally {
      this.trySubmitPendingReplay()
      this.onSortJobSettledCallback?.()
    }
  }

  private handleWorkerFailure(): void {
    if (this._destroyed) {
      return
    }

    this._workerErrorCount++
    try {
      const failedWorker = this.worker
      failedWorker?.terminate()
      if (failedWorker) {
        recordUnifiedWorkerTermination(failedWorker)
      }
    } catch {
      // ignore terminate failures
    }

    this.workerFailed = true
    this.clearPendingReplayTimer()
    this.resetSortFailureRetryState()
    this.jobsInFlight = 0
    this.inFlightSorts.clear()
    this.pendingSortRequest = null
    this.centersSet.clear()
    this.sortBoundsCache.clear()
    this.hasNewVersion = true

    this.workerRecoveryAttempts++
    try {
      if (this.workerRecoveryAttempts > GSplatUnifiedSorter.MAX_WORKER_RECOVERY_ATTEMPTS) {
        log.error(
          `排序 worker 连续失败 ${GSplatUnifiedSorter.MAX_WORKER_RECOVERY_ATTEMPTS} 次后放弃恢复`
        )
        return
      }

      this.worker = this.createWorker()
      if (this.sortParams) {
        this.postSortParametersToWorker(this.sortParams)
      }
      this.workerRecoveredPendingResync = true
    } catch (error) {
      log.error(`排序 worker 恢复失败: ${String(error)}`)
      this.workerFailed = true
    } finally {
      this.onSortJobSettledCallback?.()
    }
  }

  /**
   * 消费 worker 重建标志；返回 true 时调用方需要重发 centers（如置 layerPlacementsDirty）。
   */
  consumeWorkerRecovered(): boolean {
    const recovered = this.workerRecoveredPendingResync
    this.workerRecoveredPendingResync = false
    return recovered
  }

  /**
   * 消费异步排序失败重试标志；返回 true 时调用方应重新提交最新排序。
   */
  consumeSortRetryNeeded(): boolean {
    const retryNeeded = this.sortRetryNeeded
    this.sortRetryNeeded = false
    if (retryNeeded && this.sortFailureParameterEpoch === this.sortParametersEpoch) {
      this.sortRetryPermitParameterEpoch = this.sortParametersEpoch
    }
    return retryNeeded
  }

  private recordSortFailure(version: number, parameterEpoch: number): void {
    if (this._destroyed || parameterEpoch !== this.sortParametersEpoch) {
      return
    }

    if (this.sortFailureParameterEpoch !== parameterEpoch) {
      this.resetSortFailureRetryState()
      this.sortFailureParameterEpoch = parameterEpoch
    }

    this.clearSortFailureRetryTimer()
    this.sortRetryNeeded = false
    this.sortRetryPermitParameterEpoch = null
    const maxRetries = GSplatUnifiedSorter.SORT_FAILURE_RETRY_BACKOFF_MS.length
    this.sortFailureCount = Math.min(this.sortFailureCount + 1, maxRetries + 1)

    const retryDelayMs =
      GSplatUnifiedSorter.SORT_FAILURE_RETRY_BACKOFF_MS[this.sortFailureCount - 1]
    if (retryDelayMs === undefined) {
      if (this.sortFailureCount === maxRetries + 1) {
        log.warn(`排序连续失败 ${this.sortFailureCount} 次，version=${version}，已停止自动重试`)
      }
      return
    }

    this.sortFailureRetryTimer = globalThis.setTimeout(() => {
      this.sortFailureRetryTimer = null
      if (
        this._destroyed ||
        this.sortFailureParameterEpoch !== parameterEpoch ||
        this.sortParametersEpoch !== parameterEpoch
      ) {
        return
      }

      this.sortRetryNeeded = true
      this.onSortJobSettledCallback?.()
    }, retryDelayMs)
  }

  private clearSortFailureRetryTimer(): void {
    if (this.sortFailureRetryTimer !== null) {
      globalThis.clearTimeout(this.sortFailureRetryTimer)
    }
    this.sortFailureRetryTimer = null
  }

  private resetSortFailureRetryState(): void {
    this.clearSortFailureRetryTimer()
    this.sortFailureParameterEpoch = null
    this.sortFailureCount = 0
    this.sortRetryNeeded = false
    this.sortRetryPermitParameterEpoch = null
  }

  /**
   * 准备排序参数（转换到相机空间）
   */
  private prepareSortParams(
    camera: THREE.Camera,
    splats: GSplatInfo[]
  ): GSplatPreparedSortParams[] {
    const result: GSplatPreparedSortParams[] = []

    camera.updateMatrixWorld(true)
    this._viewProjectionMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)

    // 获取相机的世界空间参数
    const worldCameraPos = this._tempWorldCameraPos.setFromMatrixPosition(camera.matrixWorld)
    const worldCameraDir = camera.getWorldDirection(this._tempWorldCameraDir)

    // 抵消 Three.js Camera.getWorldDirection() 的 .negate()
    worldCameraDir.negate()

    for (const splat of splats) {
      const node = splat.node
      const resource = splat.resource

      // 确保世界矩阵是最新的
      node.updateMatrixWorld(true)

      // 计算逆模型矩阵
      this._invModelMatrix.copy(node.matrixWorld).invert()

      // 转换相机位置和方向到模型空间
      const modelCameraPos = this._tempVec3_2
        .copy(worldCameraPos)
        .applyMatrix4(this._invModelMatrix)
      const modelCameraDir = this._tempVec3
        .copy(worldCameraDir)
        .transformDirection(this._invModelMatrix)

      // 计算缩放（世界空间到模型空间）
      const scale = node.matrixWorld.getMaxScaleOnAxis()

      // 计算投影偏移（Linear Sort 用）
      // PlayCanvas: offset = (modelWorldPos - cameraWorldPos) · cameraWorldDir
      const worldModelPos = this._tempWorldModelPos.setFromMatrixPosition(node.matrixWorld)
      const offset = worldModelPos.sub(worldCameraPos).dot(worldCameraDir)

      // 获取 AABB
      const aabbMin = splat.aabb.min.toArray() as [number, number, number]
      const aabbMax = splat.aabb.max.toArray() as [number, number, number]
      this._modelViewProjectionMatrix.multiplyMatrices(this._viewProjectionMatrix, node.matrixWorld)

      result.push({
        id: resource.id,
        transformedDirection: modelCameraDir.clone(),
        transformedPosition: modelCameraPos.clone(),
        offset,
        scale,
        aabbMin,
        aabbMax,
        modelMatrix: Array.from(node.matrixWorld.elements),
        modelViewProjection: Array.from(this._modelViewProjectionMatrix.elements),
      })
    }

    return result
  }

  /**
   * 应用待处理的排序结果（每帧调用一次）
   *
   * @remarks
   * PlayCanvas 策略：
   * 1. 取出待处理的结果
   * 2. 触发 'sorted' 事件
   * 3. 复用 orderData buffer
   */
  applyPendingSorted(): void {
    if (this.pendingSorted) {
      const {
        count,
        version,
        orderData,
        sortGeneration,
        submittedAtMs,
        completedAtMs,
        workerTimeMs,
        requestToSubmitLatencyMs,
        bufferReused,
      } = this.pendingSorted
      this.pendingSorted = null
      const releaseOrderData = this.createOrderDataLease(orderData)

      // 触发回调
      if (this.onSortedCallback) {
        this.onSortedCallback(count, version, orderData, sortGeneration, {
          releaseOrderData,
          submittedAtMs,
          completedAtMs,
          workerTimeMs,
          requestToSubmitLatencyMs,
          bufferReused,
        })
      } else {
        releaseOrderData()
      }
    }
  }

  private createOrderDataLease(orderData: Uint32Array): () => void {
    let released = false
    this.leasedOrderData.add(orderData)

    return () => {
      if (released) {
        return
      }

      released = true
      this.leasedOrderData.delete(orderData)
      this.releaseOrderData(orderData)
    }
  }

  /**
   * 释放 orderData 到池中（如果大小匹配）
   */
  private releaseOrderData(orderData: Uint32Array): void {
    if (orderData.length === this.bufferLength) {
      this.availableOrderData.push(orderData)
      this._orderBufferReleaseCount++
    }
  }

  /**
   * 销毁排序器
   */
  destroy(): void {
    this._destroyed = true
    this.clearPendingReplayTimer()
    this.resetSortFailureRetryState()
    this.pendingSorted = null
    this.pendingSortRequest = null
    this.centersSet.clear()
    this.sortBoundsCache.clear()
    this.availableOrderData = []
    this.leasedOrderData.clear()
    this.inFlightSorts.clear()
    this.onSortedCallback = null
    this.onPendingSortedCallback = null
    this.onSortJobSettledCallback = null
    this.sortParams = null

    // 终止 Worker
    if (this.worker) {
      this.worker.terminate()
      recordUnifiedWorkerTermination(this.worker)
    }
  }

  /**
   * 获取当前版本号
   */
  get version(): number {
    return this.currentVersion
  }

  /**
   * 获取 centers 缓存大小
   */
  get centersCacheSize(): number {
    return this.centersSet.size
  }

  get lastVisibilityCompactStats(): SortVisibilityCompactStats {
    return this._lastVisibilityCompactStats
  }

  get staleResultCount(): number {
    return this._staleResultCount
  }

  get coalescedResultCount(): number {
    return this._coalescedResultCount
  }

  /** 当前 worker 无法继续接收排序任务，且同步恢复未成功。 */
  get isWorkerUnavailable(): boolean {
    return this.workerFailed
  }

  get diagnostics(): GSplatUnifiedSorterDiagnostics {
    const pendingRequestAgeMs = this.pendingSortRequest
      ? Math.max(0, this.sortNowMs() - this.pendingSortRequest.queuedAtMs)
      : null

    return {
      submittedRequestCount: this._submittedRequestCount,
      queuedRequestCount: this._queuedRequestCount,
      replayedRequestCount: this._replayedRequestCount,
      overwrittenRequestCount: this._overwrittenRequestCount,
      rejectedRequestCount: this._rejectedRequestCount,
      staleResultCount: this._staleResultCount,
      coalescedResultCount: this._coalescedResultCount,
      workerErrorCount: this._workerErrorCount,
      lastWorkerSortTimeMs: this._lastWorkerSortTimeMs,
      totalWorkerSortTimeMs: this._totalWorkerSortTimeMs,
      pendingRequestAgeMs,
      lastRequestToSubmitLatencyMs: this._lastRequestToSubmitLatencyMs,
      orderBufferAllocCount: this._orderBufferAllocCount,
      orderBufferReuseCount: this._orderBufferReuseCount,
      orderBufferReleaseCount: this._orderBufferReleaseCount,
      pendingOrderBufferLeaseCount: this.leasedOrderData.size,
      adaptiveReplayDeferralCount: this._adaptiveReplayDeferralCount,
      adaptiveReplayTimerFireCount: this._adaptiveReplayTimerFireCount,
      adaptiveSortIntervalMs: this._lastAdaptiveSortIntervalMs,
      workerSortTimeEmaMs: this.workerSortTimeEmaMs,
      pendingReplayDelayMs:
        this.pendingReplayScheduledAtMs === null
          ? null
          : Math.max(0, this.pendingReplayScheduledAtMs - this.sortNowMs()),
    }
  }
}
